import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { track } from "../lib/analytics";

/**
 * Shared drill-down link. Every dashboard tile, KPI, mission row, business
 * card etc. wraps clickable content in <DrillLink> so we get:
 *
 * - Consistent client-side navigation (<Link>, never a full reload).
 * - Consistent analytics tracking (ui.drill_link.clicked).
 * - Keyboard access (Enter / Space).
 * - Minimum 44px tap target on mobile via `.drill-link` CSS class.
 *
 * Usage:
 *   <DrillLink to="/commandlinc/missions?business=gearlinc" event="tile.gearlinc"
 *              properties={{ business_key: "gearlinc" }}>
 *     ...content...
 *   </DrillLink>
 */
const DrillLink = forwardRef(function DrillLink(
  { to, event = "generic", properties = {}, onClick, className = "", children, testId, ...rest },
  ref,
) {
  const handle = (e) => {
    try {
      track("ui.drill_link.clicked", {
        target: to,
        event,
        route: window.location.pathname,
        viewport_kind: window.innerWidth < 768 ? "mobile" : "desktop",
        ...properties,
      });
    } catch { /* analytics never throws */ }
    onClick?.(e);
  };
  return (
    <Link
      ref={ref}
      to={to}
      onClick={handle}
      onKeyDown={(e) => {
        if (e.key === " ") {
          e.preventDefault();
          e.currentTarget.click();
        }
      }}
      className={`drill-link ${className}`.trim()}
      data-testid={testId}
      {...rest}
    >
      {children}
    </Link>
  );
});

export default DrillLink;
